import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/client';
import type { StreamDetail } from '../api/types';
import { Player } from '../components/Player';
import { bytes, dateTime, duration } from '../lib/format';

export function StreamDetailPage() {
  const { key = '' } = useParams();
  const [stream, setStream] = useState<StreamDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showPlayer, setShowPlayer] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await api.stream(key);
        if (!cancelled) {
          setStream(data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
    };
    void load();
    const timer = setInterval(() => void load(), 3000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [key]);

  if (error) return <div className="panel error-text">{error}</div>;
  if (!stream) return <p className="muted">Загрузка…</p>;

  return (
    <>
      <div className="topbar">
        <h1>
          {stream.cam_ref ? `Камера ${stream.cam_ref}` : 'Поток'} <span className="muted mono">{stream.key}</span>
        </h1>
        <Link to="/streams" className="btn shrink">
          К списку
        </Link>
      </div>

      <div className="grid-2">
        <div className="panel">
          <h2>Состояние</h2>
          <table>
            <tbody>
              <tr><td className="muted">Статус</td><td><span className={`badge ${stream.state}`}>{stream.state}</span></td></tr>
              <tr><td className="muted">Режим</td><td>{stream.mode ?? '—'}</td></tr>
              <tr><td className="muted">Профиль</td><td>{stream.profile}</td></tr>
              <tr><td className="muted">Источник</td><td className="wrap mono">{stream.src_masked}</td></tr>
              <tr><td className="muted">Объект</td><td>{stream.obj_ref ?? '—'}</td></tr>
              <tr><td className="muted">Видео</td><td>{stream.video_codec ?? '?'} {stream.width}×{stream.height} {stream.fps ? `${stream.fps} fps` : ''}</td></tr>
              <tr><td className="muted">Звук</td><td>{stream.has_audio ? (stream.audio_codec ?? 'есть') : 'нет'}</td></tr>
              <tr><td className="muted">GOP</td><td>{stream.gop_sec ? `${stream.gop_sec} с` : 'не измерен'}</td></tr>
              <tr><td className="muted">Запущен</td><td>{dateTime(stream.started_at)}</td></tr>
              <tr><td className="muted">Uptime</td><td>{duration(stream.uptimeSec)}</td></tr>
              <tr><td className="muted">Зрителей</td><td>{stream.viewers}</td></tr>
              <tr><td className="muted">Рестартов</td><td>{stream.restarts}</td></tr>
              {stream.mediamtx && (
                <tr>
                  <td className="muted">MediaMTX</td>
                  <td>
                    {stream.mediamtx.ready ? 'готов' : 'не готов'} · дорожки {stream.mediamtx.tracks.join(', ') || '—'} ·
                    принято {bytes(stream.mediamtx.bytesReceived)}
                  </td>
                </tr>
              )}
              {stream.last_error && (
                <tr><td className="muted">Ошибка</td><td className="wrap error-text mono">{stream.last_error}</td></tr>
              )}
            </tbody>
          </table>

          {stream.reasons.length > 0 && (
            <>
              <h3>Почему выбран этот режим</h3>
              <ul className="reasons">
                {stream.reasons.map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
            </>
          )}
        </div>

        <div className="panel">
          <h2>Превью</h2>
          {showPlayer ? (
            <Player src={`/internal/hls/${stream.key}/index.m3u8`} />
          ) : (
            <button onClick={() => setShowPlayer(true)}>Показать поток</button>
          )}
          <p className="muted" style={{ fontSize: 12 }}>
            Превью идёт напрямую из MediaMTX и не создаёт сессию зрителя — на счётчики и учёт по токенам не влияет.
          </p>
        </div>
      </div>

      <div className="panel">
        <h2>Лог ffmpeg</h2>
        {stream.ffmpegLog.length === 0 ? (
          <p className="muted">
            Пусто. В режиме direct ffmpeg не запускается: MediaMTX тянет источник сам.
          </p>
        ) : (
          <pre>{stream.ffmpegLog.join('\n')}</pre>
        )}
      </div>

      <div className="panel">
        <h2>Сессии зрителей</h2>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Токен</th>
                <th>IP</th>
                <th>Клиент</th>
                <th>Начало</th>
                <th>Последний запрос</th>
                <th className="num">Запросов</th>
                <th className="num">Отдано</th>
              </tr>
            </thead>
            <tbody>
              {stream.sessions.length === 0 && (
                <tr>
                  <td colSpan={7} className="muted">Сессий нет.</td>
                </tr>
              )}
              {stream.sessions.map((s) => (
                <tr key={s.id}>
                  <td className="mono">{s.token_id}</td>
                  <td className="mono">{s.client_ip}</td>
                  <td className="wrap muted">{s.user_agent}</td>
                  <td>{dateTime(s.started_at)}</td>
                  <td>{dateTime(s.last_seen_at)}</td>
                  <td className="num">{s.requests}</td>
                  <td className="num">{bytes(s.bytes_out)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
